/**
 * ShopifyOrdersPage — read-only Shopify order lookup for staff.
 *
 * Exists so CS can answer "where is my device?" without a Shopify admin seat, which otherwise means
 * paying for a login that also grants the right to EDIT orders. Gated to the troubleshoot tier
 * (root/engineer/support); the backend re-checks via AdminRoleGuard.
 *
 * SEARCH-FIRST, not browse-first. With full order history the store's entire lifetime is reachable,
 * so the unfiltered view is explicitly labelled "most recent" rather than presented as the whole
 * list — and reaching an old order is a search, never paging back through thousands of rows.
 */
import { useState } from "react";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@staff-app/ui";
import type {
  ShopifyFinancialFilter,
  ShopifyFulfillmentFilter,
  ShopifyOrderRow,
} from "@staff-app/shared-types";
import { ChevronLeft, ChevronRight, RefreshCw, Search } from "lucide-react";
import { EMPTY_FILTERS, useShopifyOrders } from "../hooks/useShopifyOrders";
import { OrderTable } from "../components/OrderTable";
import { OrderDetailDrawer } from "../components/OrderDetailDrawer";

const FULFILLMENT_OPTIONS: {
  value: ShopifyFulfillmentFilter;
  label: string;
}[] = [
  { value: "any", label: "Any fulfillment" },
  { value: "unfulfilled", label: "Unfulfilled" },
  { value: "partial", label: "Partially fulfilled" },
  { value: "fulfilled", label: "Fulfilled" },
  { value: "unshipped", label: "Unshipped" },
];

const FINANCIAL_OPTIONS: { value: ShopifyFinancialFilter; label: string }[] = [
  { value: "any", label: "Any payment" },
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending" },
  { value: "authorized", label: "Authorized" },
  { value: "refunded", label: "Refunded" },
  { value: "partially_refunded", label: "Partially refunded" },
  { value: "voided", label: "Voided" },
];

export function ShopifyOrdersPage() {
  const {
    filters,
    setFilters,
    rows,
    hasNextPage,
    hasPrevPage,
    isDefaultView,
    pageIndex,
    loading,
    error,
    next,
    prev,
    refetch,
  } = useShopifyOrders();
  const [open, setOpen] = useState<ShopifyOrderRow | null>(null);

  return (
    <div
      className="flex h-full flex-col gap-4 overflow-hidden p-6"
      data-testid="shopify-orders-page"
    >
      <header className="flex shrink-0 items-start justify-between gap-4">
        {/* No heading here: the active tab already names this section, and the shell owns the
            page title. Only the description earns its place. */}
        <p className="text-sm text-muted-foreground">
          Read-only lookup of store orders — fulfillment, delivery and tracking.
          Nothing here changes an order.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={loading}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </header>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <div className="relative min-w-[18rem] flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search order #28111, email or customer…"
            value={filters.search}
            onChange={(e) =>
              setFilters((f) => ({ ...f, search: e.target.value }))
            }
            data-testid="orders-search"
          />
        </div>

        <Select
          value={filters.fulfillment}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              fulfillment: v as ShopifyFulfillmentFilter,
            }))
          }
        >
          <SelectTrigger className="w-[11rem]" data-testid="orders-fulfillment">
            <SelectValue placeholder="Any fulfillment" />
          </SelectTrigger>
          <SelectContent>
            {FULFILLMENT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.financial}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              financial: v as ShopifyFinancialFilter,
            }))
          }
        >
          <SelectTrigger className="w-[10rem]" data-testid="orders-financial">
            <SelectValue placeholder="Any payment" />
          </SelectTrigger>
          <SelectContent>
            {FINANCIAL_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Both ends, so an all-time search can be bounded rather than open-ended. */}
        <Input
          type="date"
          className="w-[9.5rem]"
          value={filters.createdFrom}
          onChange={(e) =>
            setFilters((f) => ({ ...f, createdFrom: e.target.value }))
          }
          aria-label="Created from"
          data-testid="orders-created-from"
        />
        <Input
          type="date"
          className="w-[9.5rem]"
          value={filters.createdTo}
          onChange={(e) =>
            setFilters((f) => ({ ...f, createdTo: e.target.value }))
          }
          aria-label="Created to"
          data-testid="orders-created-to"
        />

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setFilters(EMPTY_FILTERS)}
        >
          Clear
        </Button>
      </div>

      {isDefaultView && !error && (
        <p
          className="text-xs text-muted-foreground"
          data-testid="orders-default-note"
        >
          Showing the most recent orders. Search or filter to reach older ones.
        </p>
      )}

      {error && (
        <div
          className="shrink-0 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
          data-testid="orders-error"
        >
          {/* Never a blank table: an empty result and "we could not ask Shopify" look identical
              otherwise, and only one of them means the customer has no order. */}
          {error.message}
        </div>
      )}

      {/* min-h-0 + flex-1: the table region absorbs the leftover height and scrolls INSIDE itself,
          so the description, filters and pager stay put while rows move — matching Manage Coupon
          Codes. Without min-h-0 a flex child refuses to shrink below its content and the whole
          page scrolls instead. */}
      {!error && (
        <div className="flex min-h-0 flex-1 flex-col">
          <OrderTable rows={rows} loading={loading} onOpen={setOpen} />
        </div>
      )}

      <div className="flex shrink-0 items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {rows.length ? `Page ${pageIndex + 1}` : ""}
        </span>
        <div className="flex gap-2">
          {/* Cursor paging: forward from a cursor, back by popping the stack. There is no
              "jump to page N" because a Shopify connection has no offset. */}
          <Button
            variant="outline"
            size="sm"
            onClick={prev}
            disabled={!hasPrevPage || loading}
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={next}
            disabled={!hasNextPage || loading}
          >
            Next <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>

      {open && <OrderDetailDrawer row={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
